import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, CheckCircle, Users, ArrowRight, Award } from 'lucide-react';
import { sendWebhook } from '../utils/webhook';
import type { FormData } from '../types';
import SubmissionPopup from '../components/SubmissionPopup';
import MouseFollowEffect from '../components/MouseFollowEffect';

const benefits = [
  {
    icon: CheckCircle,
    title: "Personalized Strategy",
    description: "Get a custom marketing plan tailored specifically to your business goals and target audience."
  },
  {
    icon: Clock,
    title: "Fast Implementation",
    description: "Walk away with actionable steps you can implement immediately to start seeing results."
  },
  {
    icon: Users,
    title: "Expert Guidance",
    description: "Speak directly with our senior strategists who have helped brands achieve 3x+ ROI."
  },
  {
    icon: Award,
    title: "No Obligation",
    description: "Zero pressure, just valuable insights whether you decide to work with us or not."
  }
];

const testimonials = [
  {
    name: "Sarah Thompson",
    company: "Fintech Innovations",
    quote: "The strategy call was eye-opening. We implemented just two of their suggestions and saw a 40% increase in engagement within weeks.",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80"
  },
  {
    name: "Michael Chen",
    company: "GrowthBox",
    quote: "I was skeptical about what could be accomplished in a single call, but the DS Marketing team delivered more value in 30 minutes than other agencies did in months.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80"
  },
  {
    name: "Emma Rodriguez",
    company: "Sustainable Styles",
    quote: "The clarity we gained from our strategy session completely transformed our approach to content. Our conversion rate has doubled since implementing their recommendations.",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80"
  }
];

export default function Schedule() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);

    const formData: FormData = {
      email,
      type: 'consultation',
      timestamp: new Date().toISOString(),
      source: 'schedule_page'
    };

    try {
      await sendWebhook(formData);
      setEmail('');
      setShowPopup(true);
      
      // Redirect to calendar after a short delay
      setTimeout(() => {
        window.location.href = 'https://cal.com/dsmarketing.meeting.with.pascoal/ds-marketing-meeting';
      }, 1500);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="content-container">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="float-slow"
          >
            Transform Your <span className="text-golden">Marketing Strategy</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto mt-4 float-medium"
          >
            Schedule a free 30-minute strategy session with our experts and discover how to accelerate your growth
          </motion.p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          {/* Left Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <MouseFollowEffect className="bg-card-gradient p-8 rounded-2xl border border-gray-800 mb-8">
              <h2 className="text-2xl font-bold mb-6 float-medium">Book Your Free Strategy Call</h2>
              
              <div className="flex items-center mb-6 text-gray-300">
                <Calendar className="w-5 h-5 text-golden mr-3" />
                <span>30 minutes that could transform your business</span>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Email <span className="text-golden">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 transform hover:scale-105 disabled:opacity-70 disabled:hover:scale-100 float-fast"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Schedule Your Call Now'}
                </button>
              </form>
              
              <p className="text-sm text-gray-400 mt-4 text-center">
                By scheduling a call, you'll get personalized insights specific to your business needs
              </p>
            </MouseFollowEffect>
            
            <div className="bg-black/30 p-6 rounded-2xl border border-gray-800">
              <h3 className="text-xl font-bold mb-4 text-golden">What to Expect</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-golden mr-2 mt-1">1.</span>
                  <p className="text-gray-300">Brief discussion about your business and goals</p>
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2 mt-1">2.</span>
                  <p className="text-gray-300">Analysis of your current marketing approach</p>
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2 mt-1">3.</span>
                  <p className="text-gray-300">Strategic recommendations for immediate improvement</p>
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2 mt-1">4.</span>
                  <p className="text-gray-300">Q&A session to address your specific challenges</p>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Right Column - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl font-bold mb-6 float-medium">Why Book a Strategy Call?</h2>
            
            <div className="space-y-6 mb-8">
              {benefits.map((benefit, index) => (
                <div 
                  key={benefit.title}
                  className={`flex items-start space-x-4 float-${index % 2 === 0 ? 'slow' : 'medium'}`}
                >
                  <div className="bg-golden/10 p-3 rounded-full">
                    <benefit.icon className="w-6 h-6 text-golden" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-gray-300">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-gradient-to-r from-golden/20 to-black/30 p-6 rounded-2xl border border-golden/30">
              <h3 className="text-xl font-bold mb-4 text-center">Limited Availability</h3>
              <p className="text-gray-300 text-center mb-4">
                Our experts only have a few slots available each week for these high-value strategy sessions.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => document.getElementById('email')?.focus()}
                  className="inline-flex items-center px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 transform hover:scale-105 float-fast"
                >
                  Secure Your Spot <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Testimonials */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 float-slow">
            What Our Clients <span className="text-golden">Say</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-card-gradient p-6 rounded-2xl border border-gray-800 float-${index % 3 === 0 ? 'slow' : index % 3 === 1 ? 'medium' : 'fast'}`}
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h3 className="font-semibold">{testimonial.name}</h3>
                    <p className="text-sm text-gray-400">{testimonial.company}</p>
                  </div>
                </div>
                <p className="text-gray-300 italic">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl font-bold mb-4 float-slow">
            Ready to <span className="text-golden">Elevate</span> Your Marketing?
          </h2>
          <p className="text-xl text-gray-300 mb-8 float-medium">
            Don't let another day pass with underperforming marketing. Take the first step toward transforming your results.
          </p>
          <button
            onClick={() => document.getElementById('email')?.focus()}
            className="inline-flex items-center px-8 py-4 bg-golden text-white rounded-full text-lg font-semibold hover:bg-golden-600 transition-all duration-300 transform hover:scale-110 float-fast"
          >
            Book Your Free Strategy Call
          </button>
        </motion.div>
      </div>
      
      <SubmissionPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        message="Redirecting you to our booking calendar..."
      />
    </div>
  );
}